// =============================================================================
// VISITE MOBILE — le module TERRAIN (CTO 2026-09-11)
// =============================================================================
// 🔑 « Utile terrain » est le critère. Un écran desktop rétréci ⛔ N'EST PAS un
// module mobile. Ce qui change ici, et qui n'est pas cosmétique :
//
//   · UNE SECTION À LA FOIS. Le desktop empile six sections : sur un téléphone,
//     Jordan passerait son temps à chercher où il en est. Ici il avance.
//   · DES CIBLES TACTILES PLEINE LARGEUR (48px). Un visiteur a une main prise
//     par la lampe, l'échelle ou le carnet — ⛔ il ne vise pas une case de 14px.
//   · LA PHOTO EN PREMIER, pas au bout d'un menu : c'est le geste le plus
//     fréquent du terrain.
//   · LA DICTÉE sur les observations : on parle plus vite qu'on ne tape, et sur
//     un chantier on tape mal.
//   · L'ÉTAT VIENT DU SERVEUR après chaque geste. ⛔ Aucun état métier local :
//     sur un réseau qui vacille, un écran optimiste ferait croire que c'est
//     enregistré. Mieux vaut un instant d'attente qu'un relevé perdu.
// =============================================================================
import React, { useCallback, useEffect, useState } from "react";
import { Camera, Check, ChevronLeft, ChevronRight, FileText, Flag, Loader2, Plus, Square } from "lucide-react";
import MicButton from "../../components/dal/MicButton";
import ConnexionTerrain from "../../components/auth/ConnexionTerrain";
import { useAuth } from "../../contexts/AuthContext";

// 🔴 MARQUEUR DE BUNDLE — ASCII pur, et RENDU à l'écran (cf. la leçon du 09/09 :
// un marqueur non rendu est éliminé par le bundler).
export const MARQUEUR_VISITE_MOBILE = "OH-VISITE-MOBILE";

// 🔴🔴 N°508 — LE JETON DOIT VOYAGER AVEC LA REQUÊTE, ⛔ PAS SEULEMENT LE COOKIE.
//
// ⚠️ MESURÉ le 20/09 en PROD : le front terrain est servi par
// `visite.omnihub-ia.fr` mais `AuthContext` appelle le login sur
// `REACT_APP_BACKEND_URL` = `https://omnihub-ia.fr`. Le cookie `session_token`
// est posé SANS `Domain` (`marketing/routes.py:327`) ⇒ il est **host-only**
// pour `omnihub-ia.fr`. Or CES appels-ci sont RELATIFS, donc **same-origin**
// vers `visite.omnihub-ia.fr` : ⛔ le cookie n'y est JAMAIS envoyé.
// 🔑 TÉMOIN QUI A TRANCHÉ (journal nginx 17:22) : `login` et
// `platform/user/tenants` ont un `OPTIONS` préalable — donc CROSS-origin ;
// `dossiers`, `visite/profils`, `visite/saisies` n'en ont AUCUN — donc
// SAME-origin. Deux hôtes, deux portées de cookie.
// ⛔ ON N'ÉLARGIT PAS LE COOKIE AU DOMAINE PARENT : ça exposerait la session
// de l'ERP à tous les sous-domaines (raison déjà écrite dans `ConnexionTerrain`).
// ⇒ On envoie le `session_token` que `AuthContext` a déjà rangé dans
// `localStorage` — PAR ORIGINE, donc disponible ici. Le middleware l'accepte
// (`tenant_platform/middleware.py:391` : cookie OU `Bearer`).
// ✅ PROUVÉ sur preprod, sans cookie : sans auth ⇒ 401 « Authentification
// requise » ; avec `Bearer` valide ⇒ 200 + données ; avec `Bearer` inventé
// ⇒ 401 (témoin rouge — la garde discrimine).
// ⚠️ La preprod ⛔ ne peut PAS reproduire le défaut : elle sert front ET API
// sur le MÊME hôte, donc son cookie suffit. C'est la topologie PROD, scindée,
// qui le révèle.
const api = async (url, options = {}) => {
  let jeton = null;
  try { jeton = localStorage.getItem("session_token"); } catch (e) { /* stockage indisponible */ }
  const enTetes = options.body instanceof FormData
    ? {} : { "Content-Type": "application/json" };
  if (jeton) enTetes.Authorization = `Bearer ${jeton}`;
  const r = await fetch(url, {
    credentials: "include",
    headers: Object.keys(enTetes).length ? enTetes : undefined,
    ...options,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(messageErreur(j, r.status));
  return j;
};

// 🔴 UN REFUS SE LIT, ⛔ IL NE S'AFFICHE PAS EN `[object Object]`.
//
// ⚠️ MESURÉ le 18/09 sur le servi : `new Error(j?.detail)` marchait tant que
// `detail` était une CHAÎNE — c'est le cas des refus métier, qui nomment très
// bien ce qui manque (« 8 élément(s) manquant(s) · Année de construction… »).
// 🔴 MAIS TOUT 422 PYDANTIC rend `detail` sous forme de LISTE d'objets, et
// `new Error([{…}])` donne `[object Object]`. Mesuré sur 4 routes de cet
// écran : PATCH /reponse, POST /devis/proposer, POST /devis, POST /reserves.
// 🔑 LE REFUS ÉTAIT RÉEL ET PRÉCIS ; c'est l'AFFICHAGE qui le perdait.
// ⛔ On ⛔ n'avale RIEN : si le motif est illisible, on rend le statut HTTP —
// ⛔ jamais un message vide qui ferait croire que le geste a réussi.
function messageErreur(corps, statut) {
  const d = corps?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) {
    // Forme Pydantic : [{loc:[...], msg:"..."}]. On rend le CHAMP et le motif.
    const lignes = d.map((e) => {
      const champ = Array.isArray(e?.loc) ? e.loc[e.loc.length - 1] : null;
      return champ ? `« ${champ} » : ${e?.msg || "invalide"}` : (e?.msg || "");
    }).filter(Boolean);
    if (lignes.length) return lignes.join("\n");
  }
  if (d && typeof d === "object" && typeof d.message === "string") return d.message;
  return `Erreur ${statut}`;
}

// 🔑 Date et heure LISIBLES — ⛔ pas un ISO brut. Mario doit reconnaître SA
// visite d'un coup d'œil ; un horodatage technique ⛔ ne l'y aide pas.
// ⛔ Aucune dépendance ajoutée : `Intl` est dans le navigateur.
function dateHeure(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit",
                                     hour: "2-digit", minute: "2-digit" });
}

const T = {
  page: { background: "var(--oh-bg)", color: "var(--oh-text)",
          minHeight: "100vh", paddingBottom: 88 },
  barre: { position: "sticky", top: 0, zIndex: 10,
           background: "var(--oh-bg-elevated)",
           borderBottom: "0.5px solid var(--oh-border)", padding: "12px 16px" },
  carte: { background: "var(--oh-bg-elevated)",
           border: "0.5px solid var(--oh-border)", borderRadius: 12,
           padding: 16, margin: "12px 16px" },
  // 🔑 48px : la cible tactile minimale qui reste atteignable d'une main.
  tactile: { minHeight: 48, borderRadius: 12, width: "100%",
             display: "flex", alignItems: "center", gap: 10, padding: "0 14px",
             fontSize: 16, border: "0.5px solid var(--oh-border)",
             background: "var(--oh-bg-surface)", color: "var(--oh-text)" },
  principal: { background: "var(--oh-accent-solid)",
               color: "var(--oh-accent-contrast)", border: "none" },
  pied: { position: "fixed", bottom: 0, left: 0, right: 0,
          background: "var(--oh-bg-elevated)",
          borderTop: "0.5px solid var(--oh-border)", padding: 12,
          display: "flex", gap: 10 },
};

export default function MobileVisitePage() {
  // 🔴 N°474/N°475 — L'ABSENCE DE SESSION EST UN PARCOURS, ⛔ PAS UN MUR.
  // ⚠️ MESURÉ : sur `visite.omnihub-ia.fr`, l'écran affichait « Authentification
  // requise » et ⛔ RIEN D'AUTRE — Mario n'avait AUCUN moyen de se connecter.
  // Le 401 est CORRECT et ⛔ n'est pas touché ; c'est l'UX qui manquait.
  // 🔑 `checkAuth()` interroge LE SERVEUR (`GET /auth/me`) : le signal est donc
  // l'état RÉEL de la session, ⛔ pas un drapeau local qui survivrait à son
  // expiration. Une session expirée redonne donc l'écran de connexion, ⛔ pas
  // un message mort.
  const { isAuthenticated, loading: authEnCours } = useAuth();
  // 🔴🔴 N°498 — POURQUOI LE LOGIN ÉTAIT **MUET**, mesuré le 20/09 en PROD.
  //
  // ⚠️ `loading` d'`AuthContext` est PARTAGÉ : `checkAuth()` le lève au
  // démarrage, MAIS `loginWithEmail` le lève AUSSI pendant la soumission.
  // Or ci-dessous, `authEnCours` rendait le spinner À LA PLACE du formulaire.
  // ⇒ Cliquer « Se connecter » DÉMONTAIT `ConnexionTerrain`. Au retour,
  // `setErreur(...)` s'appliquait à un composant MORT, puis un formulaire
  // NEUF se remontait : ⛔ AUCUN message, ⛔ ET LES CHAMPS VIDÉS.
  // 🔑 MESURÉ AU NAVIGATEUR : mauvais mot de passe ET bon mot de passe
  // donnaient le MÊME écran — formulaire vierge, silence complet. JJ a vu
  // exactement ça, et il ⛔ ne pouvait RIEN en déduire.
  // ⇒ LE SPINNER NE SERT QU'AU **PREMIER** CONTRÔLE DE SESSION. Ensuite, le
  // formulaire RESTE MONTÉ et affiche lui-même son état (`occupe`) et son
  // erreur. ⛔ Aucune règle d'authentification touchée : le 401 reste le 401.
  const [authVerifiee, setAuthVerifiee] = useState(false);
  useEffect(() => { if (!authEnCours) setAuthVerifiee(true); }, [authEnCours]);
  const [dossiers, setDossiers] = useState([]);
  const [profils, setProfils] = useState([]);
  const [visite, setVisite] = useState(null);
  const [profil, setProfil] = useState(null);
  const [etape, setEtape] = useState(0);
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [enCours, setEnCours] = useState([]);

  // 🔴 LE MANIFEST DE **CETTE** PWA — ⛔ PAS CELUI DE L'ERP.
  //
  // ⚠️ MESURÉ (17/09) : le manifest global sert `start_url: "/"` et
  // `scope: "/"`. Installée sur l'écran d'accueil, la PWA ouvrait donc LE
  // TABLEAU DE BORD ERP — Mario atterrissait au mauvais endroit, alors que son
  // parcours est ICI. ⛔ Changer le manifest GLOBAL aurait cassé l'installation
  // ERP de ceux qui s'en servent au bureau.
  // 🔑 ON RÉUTILISE LE MOTIF DÉJÀ EN PLACE : `MobileDALPage` échange déjà le
  // lien vers `dal-manifest.json` au montage. Même geste, manifest dédié,
  // `scope: "/mobile/"`. ⛔ Aucun nouveau mécanisme.
  useEffect(() => {
    try {
      const lien = document.querySelector('link[rel="manifest"]');
      if (lien) lien.href = "/visite-manifest.json";
    } catch (e) { /* DOM indisponible — l'écran reste utilisable */ }
  }, []);

  // 🔴🔴 N°501 — CE CHARGEMENT ⛔ NE DOIT PAS PARTIR AVANT LA SESSION.
  //
  // ⚠️ MESURÉ le 20/09 sur le servi : les hooks s'exécutent AVANT les `return`
  // conditionnels, donc ce `useEffect` tirait ses 3 appels DÈS LE PREMIER
  // MONTAGE — c'est-à-dire PENDANT l'écran de connexion, sans session.
  // ⇒ **3 × 401**, `setErreur("Authentification requise")`, et comme la
  // dépendance était `[]`, ⛔ RIEN ⛔ n'était JAMAIS rechargé ensuite.
  // 🔴 CE QUE MARIO VOYAIT : il se connecte… et atterrit sur un écran QUI DIT
  // « Authentification requise », ⛔ SANS un seul dossier ni profil. Le login
  // avait pourtant RÉUSSI — les mêmes appels rejoués une seconde plus tard
  // rendent **200** avec toutes les données. ⛔ Ce n'était PAS un refus : un
  // écran qui ⛔ ne redemande jamais ce qu'il a demandé trop tôt.
  // 🔑 En PROD ce mur était MASQUÉ par le blocage CORS (N°498) : on n'allait
  // pas jusque-là. C'est le geste rouge SUIVANT de la chaîne V1.
  // ⇒ On attend `isAuthenticated`, et on RECHARGE quand il bascule.
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const [d, p, c] = await Promise.all([
          api("/api/metier/dossiers?limit=50"),
          api("/api/visite/profils"),
          // 🔴 CE QUI EST DÉJÀ EN COURS. ⛔ Sans ça, l'écran ⛔ ne PEUT PAS
          // savoir qu'une visite attend, et Mario ⛔ non plus.
          api("/api/visite/saisies"),
        ]);
        setDossiers(Array.isArray(d) ? d : d?.dossiers || []);
        setProfils(p.profils || []);
        setEnCours(c?.en_cours || []);
        // 🔑 Un chargement réussi EFFACE l'erreur du tour précédent : sinon un
        // « Authentification requise » périmé survivrait à la connexion.
        setErreur(null);
      } catch (e) { setErreur(e.message); }
    })();
  }, [isAuthenticated]);

  const relire = useCallback(async (id) => {
    setVisite(await api(`/api/visite/saisies/${id}`));
  }, []);

  const demarrer = async (dossierId, code, devisId) => {
    setErreur(null); setOccupe(true);
    try {
      const v = await api("/api/visite/saisies", {
        method: "POST",
        body: JSON.stringify({ dossier_id: dossierId, profil_code: code,
                               devis_id: devisId || null }),
      });
      setProfil(await api(`/api/visite/profils/${code}`));
      await relire(v.id);
      setEtape(0);
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  // 🔑 LE NOM DU BÉNÉFICIAIRE — LU, ⛔ jamais RECOMPOSÉ ici.
  // ⚠️ `client_nom` est servi par la lecture canonique des dossiers (elle le
  // dérive quand le cache est vide). Le recomposer dans cet écran créerait une
  // SECONDE façon de fabriquer un nom — et deux implémentations de la même
  // logique divergent toujours. ⇒ On APPARIE, on ⛔ ne CALCULE pas.
  // `saisie.dossier_id` vaut `str(dossier._id)` — mesuré, ⛔ pas supposé.
  const nomDossier = (id) => {
    const d = dossiers.find((x) => String(x.id || x._id) === String(id));
    return (d && (d.client_nom || "").trim()) || "";
  };

  // 🔑 REPRENDRE ⛔ n'est PAS « démarrer » : on ⛔ ne repasse PAS par POST
  // /saisies. On relit la visite existante par son id — ⛔ aucun risque de
  // doublon, et ⛔ aucun appel qui pourrait échouer sur un profil absent.
  const reprendre = async (v) => {
    setErreur(null); setOccupe(true);
    try {
      setProfil(await api(`/api/visite/profils/${v.profil_code}`));
      await relire(v.id);
      setEtape(0);
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  // 🔑 La sélection PART AU SERVEUR, et l'écran relit ce qui est ENREGISTRÉ —
  // ⛔ il ⛔ ne se fie PAS à son propre state. Une sélection qui vivrait en
  // mémoire disparaîtrait à la première photo (l'appareil natif recharge la
  // page) : c'est exactement le défaut qu'on a payé sur la dictée.
  const retenir = async (ids) => {
    setErreur(null); setOccupe(true);
    try {
      await api(`/api/visite/saisies/${visite.id}/sections`, {
        method: "PATCH", body: JSON.stringify({ sections: ids }),
      });
      await relire(visite.id);
      setEtape(0);
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  const agir = async (fn) => {
    setErreur(null);
    try { await fn(); await relire(visite.id); }
    catch (e) { setErreur(e.message); }
  };

  // ⏸️ On ATTEND la réponse du serveur avant de trancher : afficher le
  // formulaire pendant la vérification ferait clignoter un écran de connexion
  // devant quelqu'un DÉJÀ connecté.
  if (authEnCours && !authVerifiee) {
    return (
      <div style={{ ...T.page, display: "flex", alignItems: "center",
                    justifyContent: "center" }}
           data-testid="mobile-auth-attente">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) return <ConnexionTerrain titre="Visite terrain" />;

  // ── CHOIX DU DOSSIER ET DU TYPE DE VISITE ──────────────────────────
  if (!visite) {
    return (
      <div style={T.page} data-testid="mobile-visite">
        <div style={T.barre}>
          <div style={{ fontWeight: 500, fontSize: 18 }}>Visite terrain</div>
          <span style={{ fontSize: 10, opacity: 0.4 }}>{MARQUEUR_VISITE_MOBILE}</span>
        </div>
        {erreur && <Erreur texte={erreur} />}
        {/* 🔴 CE QUI EST EN COURS, EN PREMIER ET EN HAUT.
            ⚠️ Mesuré sur un vrai téléphone : après un simple verrouillage,
            l'écran affichait « Démarrer la visite » et 39 références nues, et
            ⛔ AUCUN mot ne disait qu'une visite attendait — alors que le
            travail ÉTAIT enregistré (réserves et observations en base).
            ⇒ Mario devait SAVOIR DE MÉMOIRE que « Démarrer » reprend son
            brouillon, et retrouver son dossier parmi 39. Une information
            DISPONIBLE transformée en travail de recherche.
            🔑 On DIT aussi ce qui est déjà saisi : sans le compte, « reprendre »
            ⛔ ne rassure PAS quelqu'un qui craint d'avoir tout perdu. */}
        {enCours.length > 0 && (
          <div style={{ ...T.carte,
                        border: "1px solid var(--oh-accent)" }}
               data-testid="mobile-en-cours">
            {/* 🔴 RASSURER D'ABORD, EN TOUTES LETTRES. Quelqu'un qui rallume son
                téléphone craint d'avoir tout perdu : la première ligne doit le
                démentir, ⛔ pas le laisser déduire d'un bouton « Reprendre ». */}
            <div style={{ fontWeight: 500, marginBottom: 2 }}>
              {enCours.length === 1
                ? "1 visite en cours" : `${enCours.length} visites en cours`}
              {" — votre travail a été sauvegardé"}
            </div>
            <div style={{ fontSize: 12, color: "var(--oh-text-secondary)",
                          marginBottom: 10 }}>
              Reprenez où vous en étiez.
            </div>
            {enCours.map((v) => (
              <button key={v.id}
                      style={{ ...T.tactile, ...T.principal, width: "100%",
                               flexDirection: "column", alignItems: "flex-start",
                               height: "auto", paddingTop: 10, paddingBottom: 10,
                               gap: 2, marginBottom: 8, textAlign: "left" }}
                      data-testid="mobile-reprendre"
                      disabled={occupe}
                      onClick={() => reprendre(v)}>
                {/* 🔴 LE DOSSIER DOIT ÊTRE IDENTIFIABLE, ⛔ pas une référence
                    nue : mesuré, 39 « MARI-DOS-2026-000XX » ⛔ ne permettent PAS
                    de retrouver le sien.
                    🔑 Le nom vient de `client_nom`, servi par la lecture
                    canonique des dossiers — ⛔ on ⛔ ne REFAIT PAS la dérivation
                    ici : une seconde façon de composer un nom divergerait. */}
                <span style={{ fontWeight: 500 }}>
                  {nomDossier(v.dossier_id) || v.dossier_reference
                   || v.dossier_id}
                </span>
                <span style={{ fontSize: 12, opacity: 0.85 }}>
                  {nomDossier(v.dossier_id) ? `${v.dossier_reference} · ` : ""}
                  {/* 🔴 N°465 — DATE ET HEURE. ⚠️ Sans elles, Mario ⛔ ne sait
                      PAS CE QU'IL REPREND : « Démarrer » sur un dossier déjà
                      commencé ROUVRE la visite existante (ouvrir() est
                      idempotent, et c'est VOULU). C'est cette reprise
                      silencieuse qui lui a fait croire que le rapport était
                      figé — le rapport suivait bien sa visite, c'était LA MÊME
                      VISITE. On rend l'état VISIBLE, ⛔ on ⛔ ne casse PAS le
                      mécanisme. */}
                  {dateHeure(v.updated_at)} · {v.nb_reponses} réponse(s)
                  {v.nb_reserves > 0 && ` · ${v.nb_reserves} réserve(s)`}
                  {v.nb_bloquantes > 0 && ` dont ${v.nb_bloquantes} bloquante(s)`}
                </span>
              </button>
            ))}
          </div>
        )}
        {profils.map((p) => (
          <div key={p.code} style={T.carte}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>{p.libelle}</div>
            <div style={{ color: "var(--oh-text-secondary)", fontSize: 13,
                          marginBottom: 12 }}>
              {p.nb_sections} étape(s) · {p.nb_questions} point(s) de contrôle
            </div>
            <ChoixDossier dossiers={dossiers} profil={p} occupe={occupe}
                          onDemarrer={demarrer} />
          </div>
        ))}
      </div>
    );
  }

  // ── 🔴 LA SÉLECTION DES TRAVAUX — PREMIER TEMPS DU GUIDE MARIO ──────────
  // 🔑 « Sélectionnez visuellement les familles de travaux de la visite. Seules
  // les parties retenues sont ensuite parcourues. » — guide, §1.1.
  //
  // ⚠️ LA MÊME RÈGLE QUE LE SERVEUR, ⛔ PAS UNE SECONDE : `sections_retenues`
  // absent ⇒ tout est retenu (comportement d'avant, donc ⛔ aucune régression
  // pour TEF) ; présent ⇒ lui seul décide. Le serveur applique EXACTEMENT
  // ça dans `sections_du_parcours()`, qui pilote aussi les photos exigées et
  // le rapport. ⛔ Il ⛔ n'existe PAS d'autorité photo indépendante.
  const activables = (profil?.sections || []).filter((x) => x.activable);
  const sections = (profil?.sections || []).filter(
    (x) => !x.activable
           || !visite.sections_retenues
           || visite.sections_retenues.includes(x.id));

  // 🔴 L'ÉCRAN DE SÉLECTION S'IMPOSE TANT QUE LE CHOIX ⛔ N'A PAS ÉTÉ FAIT.
  // ⚠️ Et il ⛔ ne s'affiche QUE si le profil OFFRE des travaux sélectionnables :
  // un profil sans `activable` garde son parcours inchangé.
  if (activables.length > 0 && !visite.sections_retenues) {
    return <ChoixTravaux visite={visite} activables={activables}
                         occupe={occupe} erreur={erreur}
                         onRetenir={retenir} />;
  }

  const s = sections[etape];
  const derniere = etape >= sections.length - 1;
  const photos = (visite.photos || {})[s?.id] || [];

  return (
    <div style={T.page} data-testid="mobile-visite-en-cours">
      <div style={T.barre}>
        <div style={{ fontSize: 12, color: "var(--oh-text-secondary)" }}>
          {visite.dossier_reference}
        </div>
        <div style={{ fontWeight: 500, fontSize: 17 }}>{s?.libelle}</div>
        {/* 🔑 « Où j'en suis » — sur un téléphone, c'est la première question. */}
        <div style={{ fontSize: 12, color: "var(--oh-text-secondary)" }}
             data-testid="mobile-progression">
          Étape {etape + 1} sur {sections.length}
        </div>
      </div>

      {erreur && <Erreur texte={erreur} />}

      <div style={T.carte}>
        {/* 🔴 LA PHOTO EN PREMIER — le geste le plus fréquent du terrain. */}
        <label style={{ ...T.tactile, ...T.principal, marginBottom: 12,
                        justifyContent: "center" }}
               data-testid="mobile-photo">
          <Camera size={20} />
          {photos.length ? `Ajouter une photo (${photos.length})` : "Prendre une photo"}
          <input type="file" accept="image/*" capture="environment"
                 style={{ display: "none" }}
                 aria-label={`Photo pour ${s?.libelle}`}
                 onChange={(e) => {
                   const f = e.target.files?.[0];
                   e.target.value = "";
                   if (!f) return;
                   const corps = new FormData();
                   corps.append("section_id", s.id);
                   corps.append("fichier", f);
                   agir(() => api(`/api/visite/saisies/${visite.id}/photos`,
                                  { method: "POST", body: corps }));
                 }} />
        </label>

        {(s?.questions || []).map((q) => (
          <Question key={q.id} q={q} section={s} visite={visite}
                    onRepondre={(valeur, quantite) => agir(() =>
                      api(`/api/visite/saisies/${visite.id}/reponse`, {
                        method: "PATCH",
                        body: JSON.stringify({ section_id: s.id,
                                               question_id: q.id, valeur,
                                               quantite: quantite ?? null }),
                      }))} />
        ))}

        <Observations visite={visite} section={s} onEnregistrer={(texte) =>
          agir(() => api(`/api/visite/saisies/${visite.id}/observation`, {
            method: "PATCH",
            body: JSON.stringify({ section_id: s.id, texte }),
          }))} />

        {(visite.reserves_config || {}).actives && (
          <Reserves visite={visite} section={s}
                    config={visite.reserves_config}
                    onAjouter={(libelle, gravite) => agir(() =>
                      api(`/api/visite/saisies/${visite.id}/reserves`, {
                        method: "POST",
                        body: JSON.stringify({ libelle, gravite,
                                               section_id: s.id }),
                      }))} />
        )}
      </div>

      {/* 🔴 LE GESTE DEVIS — seulement quand la visite PRODUIT le devis.
          ⛔ Pour une recherche de fuite, le devis est l'ENTRÉE : l'offrir ici
          inverserait le parcours métier. Le sens est DÉCLARÉ sur la visite,
          ⛔ il n'est pas déduit du profil ni du tenant. */}
      {/* 🔑 L'ANCRE de fin de visite — c'est ICI que « Terminer » emmène.
          ⛔ Pas un composant de plus : un repère, pour que la suite du
          parcours cesse d'être cachée sous la barre fixe. */}
      {derniere && <div data-testid="mobile-fin-visite" />}

      {derniere && visite.sens_devis === "sortie" && (
        <DevisParDictee visite={visite} onErreur={setErreur}
                        onRelire={() => relire(visite.id)} />
      )}

      {derniere && <Cloture visite={visite} onCloturer={() => agir(() =>
        api(`/api/visite/saisies/${visite.id}/cloturer`, { method: "POST" }))} />}

      <div style={T.pied}>
        <button style={{ ...T.tactile, flex: 1, justifyContent: "center" }}
                disabled={etape === 0} data-testid="mobile-precedent"
                onClick={() => setEtape((e) => Math.max(0, e - 1))}>
          <ChevronLeft size={20} /> Précédent
        </button>
        {/* 🔴 À LA DERNIÈRE ÉTAPE, « SUIVANT » DEVIENT « TERMINER ».
            ⚠️ CE QUI SE PASSAIT, mesuré sur le servi le 18/09 : à « Étape 6 sur
            6 » le bouton était `disabled` — il n'y a pas d'étape 7 — MAIS il
            restait peint en BLEU PLEIN (`T.principal` appliqué sans condition).
            Il avait donc l'air cliquable, ⛔ ne répondait PAS, et ⛔ RIEN ⛔ ne
            disait pourquoi. La suite du parcours (clôture, rapport, devis) est
            PLUS BAS, hors de vue derrière cette barre fixe.
            🔑 AFFORDANCE MENSONGÈRE : un bouton qui paraît actif et ne fait
            rien apprend à l'utilisateur que l'écran est cassé. Ici il EMMÈNE
            vers la suite au lieu de mourir. ⛔ Aucune règle métier touchée. */}
        <button style={{ ...T.tactile, ...T.principal, flex: 1,
                         justifyContent: "center" }}
                data-testid="mobile-suivant"
                onClick={() => {
                  if (!derniere) {
                    setEtape((e) => Math.min(sections.length - 1, e + 1));
                    return;
                  }
                  const fin = document.querySelector('[data-testid="mobile-fin-visite"]');
                  if (fin) fin.scrollIntoView({ behavior: "smooth", block: "start" });
                }}>
          {derniere ? "Terminer" : "Suivant"} <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
}

// 🔴 LE DERNIER MAILLON DU GESTE DEVIS — dictée → proposition → VALIDATION
// HUMAINE → lignes → devis.
//
// 🔑 CE QUE CET ÉCRAN GARANTIT, et c'est tout son objet : AUCUNE LIGNE N'EST
//    CRÉÉE TANT QUE MARIO N'A PAS TRANCHÉ. Il voit le produit apparié, sa
//    référence, sa quantité, son unité, son PRIX CATALOGUE et sa TVA — et il
//    peut CORRIGER ou REFUSER chaque ligne AVANT que quoi que ce soit existe.
//
// ⛔ CE QU'IL ⛔ N'INVENTE JAMAIS :
//    · une quantité absente reste VIDE — ⛔ jamais `1`, qui produirait un devis
//      plausible et faux que personne ⛔ ne saurait relire ;
//    · un énoncé sans produit correspondant ⛔ NE DEVIENT PAS une ligne — on le
//      DIT. ⚠️ C'est le bon comportement, ⛔ pas un échec : si Mario dicte une
//      prestation absente de SON catalogue, l'inventer serait la faute.
//    · le PRIX ⛔ n'est JAMAIS envoyé par le navigateur : on transmet la
//      RÉFÉRENCE et la QUANTITÉ, le serveur relit le reste au catalogue.
function DevisParDictee({ visite, onErreur, onRelire }) {
  // 🔴 LA DICTÉE VIENT DE LA BASE, ⛔ PLUS D'UN ÉTAT VIDE.
  //
  // ⚠️ CE QUI SE PASSAIT, mesuré le 18/09 : ce `useState("")` était le SEUL
  // état métier local de tout l'écran. Le technicien dictait ses travaux,
  // PRENAIT UNE PHOTO — l'appareil natif reprend la main, la page recharge —
  // et LA DICTÉE DISPARAISSAIT. « Terminer » ⛔ ne faisait alors plus rien :
  // il ⛔ n'avait PLUS RIEN à proposer.
  // 🔑 « Un état qui ⛔ ne survit pas à un rechargement ⛔ n'existe pas. »
  // ⚠️ Défaut que j'ai introduit dans un fichier dont l'en-tête dit
  // « ⛔ AUCUN ÉTAT MÉTIER LOCAL ». Écrit, puis contredit.
  const initial = (visite.dictee_devis || {}).texte || "";
  const [texte, setTexte] = useState(initial);
  // Resynchronise quand le serveur rend une autre valeur (reprise, rechargement).
  useEffect(() => { setTexte(initial); }, [initial]);
  const [prop, setProp] = useState(null);
  const [choix, setChoix] = useState({});
  const [devis, setDevis] = useState(null);
  const [occupe, setOccupe] = useState(false);
  // 🔑 LE CATALOGUE DU TENANT — pour que Mario puisse CHOISIR son poste quand
  // l'appariement ⛔ n'en propose AUCUN. ⛔ Lecture seule, ⛔ aucun moteur ici.
  const [catalogue, setCatalogue] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const c = await api("/api/metier/catalogue/produits");
        setCatalogue(Array.isArray(c) ? c : c?.produits || []);
      } catch (e) {
        // ⛔ Un catalogue indisponible ⛔ ne DOIT PAS casser la dictée : la
        // liste reste vide et l'état de la ligne le dit déjà.
      }
    })();
  }, []);

  // 🔴 TEMPS 4 DU GUIDE — « COMPLÉTER ». N°469-2 / N°485.
  // ⚠️ « confirmer, corriger, supprimer » existaient ; AJOUTER ⛔ n'existait
  // PAS. Or Mario voit sur place ce qu'il n'a pas dicté — et sans ce geste il
  // devait relancer une dictée, ou finir son devis au bureau.
  // 🔑 MÊME ÉCRAN QUE « choisir le poste catalogue », et ⛔ ce n'est pas une
  // coïncidence : une ligne ajoutée n'a JAMAIS de produit apparié, donc elle
  // passe par le MÊME geste de choix. Une seconde mécanique aurait divergé.
  // ⛔ AUCUN appel au moteur d'appariement : une ligne ajoutée est décrite ET
  // choisie PAR MARIO, de bout en bout.
  const [ajouts, setAjouts] = useState([]);
  const majAjout = (i, patch) =>
    setAjouts((l) => l.map((x, k) => k === i ? { ...x, ...patch } : x));

  const majChoix = (i, patch) =>
    setChoix((c) => ({ ...c, [i]: { ...(c[i] || {}), ...patch } }));

  // La référence retenue pour la ligne i : celle que Mario a choisie, sinon
  // celle que l'appariement propose. ⛔ `null` si aucune — la ligne est alors
  // impossible, et l'écran le dit.
  const refDe = (p, i) => choix[i]?.reference ?? (p.produit?.reference || null);
  const qteDe = (p, i) => {
    const v = choix[i]?.quantite;
    if (v !== undefined) return v;
    return p.quantite ?? "";
  };
  const exclue = (i) => !!choix[i]?.exclue;

  const retenues = (prop?.propositions || [])
    .map((p, i) => ({ p, i }))
    .filter(({ p, i }) => !exclue(i) && refDe(p, i)
                          && Number(qteDe(p, i)) > 0)
    .map(({ p, i }) => ({
      produit_reference: refDe(p, i),
      quantite: Number(qteDe(p, i)),
      designation: p.enonce,
    }))
    // 🔑 MÊME CONTRAT que les lignes dictées : une référence catalogue, une
    // quantité > 0, et LA DESCRIPTION DE MARIO. Le serveur ⛔ ne voit aucune
    // différence — donc ⛔ aucune règle à dupliquer côté serveur.
    .concat(ajouts
      .filter((a) => a.reference && Number(a.quantite) > 0 && a.enonce.trim())
      .map((a) => ({
        produit_reference: a.reference,
        quantite: Number(a.quantite),
        designation: a.enonce.trim(),
      })));

  // 🔑 MÊME MOTIF QUE LES OBSERVATIONS : on envoie au serveur quand
  // l'utilisateur QUITTE le champ. ⛔ Pas à chaque frappe.
  const persister = async (t) => {
    try {
      await api(`/api/visite/saisies/${visite.id}/dictee-devis`, {
        method: "PATCH", body: JSON.stringify({ texte: t }),
      });
      onRelire && onRelire();
    } catch (e) { onErreur(e.message); }
  };

  const proposer = async () => {
    setOccupe(true); setDevis(null);
    // ⚠️ On PERSISTE AVANT de proposer : si l'utilisateur clique sans avoir
    // quitté le champ, `onBlur` ⛔ n'a PAS encore tiré.
    if (texte !== initial) { await persister(texte); }
    try {
      const r = await api(`/api/visite/saisies/${visite.id}/devis/proposer`,
                          { method: "POST",
                            body: JSON.stringify({ texte }) });
      setProp(r); setChoix({}); setAjouts([]);
    } catch (e) { onErreur(e.message); } finally { setOccupe(false); }
  };

  const creer = async () => {
    setOccupe(true);
    try {
      const r = await api(`/api/visite/saisies/${visite.id}/devis`,
                          { method: "POST",
                            body: JSON.stringify({ lignes_validees: retenues }) });
      setDevis(r);
    } catch (e) { onErreur(e.message); } finally { setOccupe(false); }
  };

  if (visite.devis_id && !devis) {
    return (
      <div style={T.carte} data-testid="mobile-devis-deja">
        <div style={{ fontWeight: 500 }}>Le devis de cette visite existe déjà</div>
        <div style={{ color: "var(--oh-text-secondary)", fontSize: 13,
                      marginTop: 4, lineHeight: 1.6 }}>
          Une visite produit un seul devis. Il se consulte dans le dossier
          {visite.dossier_reference ? ` ${visite.dossier_reference}` : ""},
          section Devis.
        </div>
      </div>
    );
  }

  if (devis) {
    const d = devis.devis || {};
    return (
      <div style={T.carte} data-testid="mobile-devis-cree">
        {/* 🔴 LES NOMS DE CHAMPS SONT MESURÉS, ⛔ PAS SUPPOSÉS. Ma première
            version lisait `reference`, `numero` et `montant_ht` : MESURÉ en
            base, `reference` et `montant_ht` ⛔ N'EXISTENT PAS, et `numero`
            vaut `null` tant que le devis est un BROUILLON. Le panneau aurait
            annoncé « Devis  créé » — sans numéro et sans total.
            🔑 Les vrais champs : `numero_provisoire` (DRAFT-000NN) puis
            `numero` à la validation, et `total_ht` / `total_ttc`. */}
        <div style={{ background: "var(--oh-stat-green-bg)",
                      color: "var(--oh-stat-green-text)", borderRadius: 8,
                      padding: 10, marginBottom: 10 }}>
          <div style={{ fontWeight: 500 }} data-testid="mobile-devis-numero">
            Devis {d.numero || d.numero_provisoire || ""} créé —{" "}
            {devis.lignes_creees} ligne(s)
          </div>
          {d.total_ht !== undefined && d.total_ht !== null && (
            <div style={{ fontSize: 13, marginTop: 2 }}
                 data-testid="mobile-devis-total">
              Total {d.total_ht} € HT
              {d.total_ttc !== undefined && d.total_ttc !== null
                ? ` · ${d.total_ttc} € TTC` : ""}
            </div>
          )}
          {/* ⚠️ ON DIT QUE C'EST UN BROUILLON. Laisser croire à un devis
              définitif ferait envoyer au client un document non validé. */}
          {d.statut === "draft" && (
            <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>
              Brouillon — à relire et valider dans OmniHub avant envoi.
            </div>
          )}
        </div>
        {/* ⚠️ CE QUI N'A PAS ÉTÉ REPRIS EST DIT. Un devis créé ET incomplet
            ⛔ ne doit PAS passer pour complet. */}
        {(devis.empeches || []).length > 0 && (
          <ul style={{ margin: "0 0 8px", paddingLeft: 16, fontSize: 13,
                       color: "var(--oh-stat-amber-text)" }}
              data-testid="mobile-devis-empeches">
            {devis.empeches.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        )}
        <div style={{ color: "var(--oh-text-secondary)", fontSize: 13,
                      lineHeight: 1.6 }}>
          Il est consultable dans OmniHub, dossier
          {visite.dossier_reference ? ` ${visite.dossier_reference}` : ""},
          section Devis.
        </div>
      </div>
    );
  }

  return (
    <div style={T.carte} data-testid="mobile-devis-dictee">
      <div style={{ fontWeight: 500, marginBottom: 8 }}>
        Devis — dicter les travaux
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <textarea value={texte} onChange={(e) => setTexte(e.target.value)}
                  rows={3} aria-label="Travaux dictés"
                  placeholder="Dictez les travaux, un par phrase. Ex. : « Reprise de couverture, quantité 12 mètres carrés. »"
                  data-testid="mobile-devis-texte"
                  onBlur={() => { if (texte !== initial) persister(texte); }}
                  style={{ flex: 1, borderRadius: 12, padding: 10, fontSize: 16,
                           border: "0.5px solid var(--oh-border)",
                           background: "var(--oh-bg-surface)",
                           color: "var(--oh-text)" }} />
        {/* 🔑 La dictée persiste elle aussi : `onTranscript` ⛔ ne déclenche
            PAS le `onBlur` du textarea. Sans ça, un texte DICTÉ puis suivi
            d'une photo se perdait — exactement le cas de JJ. */}
        <MicButton value={texte}
                   onTranscript={(v) => { setTexte(v); persister(v); }} />
      </div>
      {/* 🔴 TEMPS 2 DU GUIDE — « Relire et corriger ». N°461 / N°485.
          ⚠️ LE CHAMP ÉDITABLE EXISTAIT DÉJÀ ; C'EST L'INVITATION QUI MANQUAIT.
          Le guide est explicite : « À la fin, OmniHub affiche le texte complet
          de votre dictée. Vérifiez le texte. Vous pouvez le modifier avant de
          continuer. L'objectif est d'abord de conserver fidèlement ce que vous
          avez dit. »
          🔑 DEUX VALIDATIONS, ⛔ PAS UNE : Mario dit d'abord « c'est bien ce
          que j'ai dit », PUIS « c'est bien ce que je veux dans mon devis ».
          Sans la première, il valide des prestations sans avoir jamais
          contrôlé le texte dont elles sortent — et c'est là que la dictée
          déformée passait inaperçue.
          ⛔ CE N'EST PAS `onRelire` : celui-là relit LA VISITE depuis le
          serveur. Un nom qui ressemble ⛔ n'est pas la chose. */}
      {texte.trim() && !prop && (
        <div style={{ marginTop: 10, borderRadius: 8, padding: 10,
                      background: "var(--oh-stat-blue-bg)",
                      color: "var(--oh-stat-blue-text)", fontSize: 13,
                      lineHeight: 1.6 }}
             data-testid="mobile-dictee-relecture">
          Relisez le texte ci-dessus. Corrigez-le si besoin : c'est lui qui
          servira à préparer vos prestations.
        </div>
      )}

      <button style={{ ...T.tactile, ...(texte.trim() ? T.principal : {}),
                       justifyContent: "center", marginTop: 10 }}
              disabled={!texte.trim() || occupe}
              data-testid="mobile-devis-proposer" onClick={proposer}>
        {occupe ? <Loader2 size={18} className="animate-spin" />
                : <>C'est bien ce que j'ai dit — préparer les prestations</>}
      </button>

      {prop && prop.propositions.length === 0 && (
        <div style={{ marginTop: 10, fontSize: 13,
                      color: "var(--oh-text-secondary)" }}
             data-testid="mobile-devis-rien">
          Rien d'exploitable n'a été reconnu dans ce texte. Aucune ligne n'a été
          créée.
        </div>
      )}

      {prop && prop.propositions.map((p, i) => {
        const ref = refDe(p, i);
        const q = qteDe(p, i);
        const cand = p.candidats || [];
        const aDepartager = (p.manques || []).includes("choix du produit");
        const sansProduit = !ref;
        const sansQuantite = !(Number(q) > 0);
        const retenu = cand.find((c) => c.reference === ref) || p.produit
                       || catalogue.find((c) => c.reference === ref);
        const etat = sansProduit
          ? ["Aucun produit correspondant — à compléter au catalogue",
             "red"]
          : aDepartager ? ["À départager", "amber"]
          : sansQuantite ? ["Quantité à renseigner", "amber"]
          : ["À valider", "blue"];
        return (
          <div key={i} data-testid="mobile-devis-proposition"
               style={{ marginTop: 10, padding: 10, borderRadius: 10,
                        border: "0.5px solid var(--oh-border)",
                        opacity: exclue(i) ? 0.45 : 1 }}>
            <div style={{ fontSize: 13, color: "var(--oh-text-secondary)" }}>
              « {p.enonce} »
            </div>
            <div style={{ display: "inline-block", marginTop: 6, fontSize: 12,
                          padding: "2px 8px", borderRadius: 8,
                          background: `var(--oh-stat-${etat[1]}-bg)`,
                          color: `var(--oh-stat-${etat[1]}-text)` }}
                 data-testid="mobile-devis-etat">{etat[0]}</div>

            {/* 🔴 PLUSIEURS CANDIDATS : Mario CHOISIT. ⛔ Le système ⛔ ne
                tranche PAS à sa place — un pari silencieux sur le produit
                est exactement ce qu'on refuse. */}
            {aDepartager && cand.length > 1 && (
              <div style={{ marginTop: 8 }}
                   data-testid="mobile-devis-departager">
                {cand.map((c) => (
                  <label key={c.reference}
                         style={{ display: "flex", alignItems: "center",
                                  gap: 8, minHeight: 40, fontSize: 14 }}>
                    <input type="radio" name={`cand-${i}`}
                           checked={ref === c.reference}
                           data-testid="mobile-devis-candidat"
                           onChange={() => majChoix(i, { reference: c.reference })} />
                    {c.designation} · {c.reference}
                  </label>
                ))}
              </div>
            )}

            {/* 🔴 AUCUN APPARIEMENT — MARIO CHOISIT SON POSTE. N°464-3 / N°469-2.
                ⚠️ MESURÉ le 20/09 sur son témoin réel (descriptif du devis
                19443) : l'appariement rend `candidats: 0` et le motif « à
                rapprocher à la main » — mais l'écran ⛔ n'offrait RIEN à
                rapprocher. Le guide dit pourtant : « si OmniHub hésite sur une
                prestation, il vous demande de choisir. Il ne doit pas inventer
                à votre place. » ⇒ Il ⛔ n'inventait pas, mais il ⛔ ne demandait
                pas non plus : la ligne était simplement PERDUE.
                🔑 LES AUTORITÉS, et elles ⛔ ne se recouvrent PAS :
                   la DICTÉE donne la description · le CATALOGUE donne le poste
                   et l'unité · MARIO arbitre · MARIO fixe le prix.
                ⛔ LE POSTE CHOISI ⛔ NE REMPLACE JAMAIS LE DESCRIPTIF DICTÉ :
                c'est `p.enonce` qui part en `designation`, ⛔ pas le libellé du
                catalogue. ⛔ Aucun changement du moteur d'appariement ici. */}
            {sansProduit && (
              <div style={{ marginTop: 8 }}
                   data-testid="mobile-devis-choisir-poste">
                <label style={{ fontSize: 13, display: "block",
                                marginBottom: 4 }}
                       htmlFor={`poste-${i}`}>
                  Choisir le poste catalogue
                </label>
                <select id={`poste-${i}`} value={ref || ""}
                        data-testid="mobile-devis-poste"
                        onChange={(e) => majChoix(i, {
                          reference: e.target.value || null })}
                        style={{ width: "100%", minHeight: 44, borderRadius: 10,
                                 padding: "0 10px", fontSize: 16,
                                 border: "0.5px solid var(--oh-border)",
                                 background: "var(--oh-bg-surface)",
                                 color: "var(--oh-text)" }}>
                  <option value="">— Aucun poste choisi —</option>
                  {catalogue.map((c) => (
                    <option key={c.reference} value={c.reference}>
                      {c.designation} · {c.reference}
                      {c.unite ? ` (${c.unite})` : ""}
                    </option>
                  ))}
                </select>
                {/* 🔑 On DIT que la description reste celle de Mario : sinon il
                    croirait que choisir un poste écrase ce qu'il a dicté. */}
                <div style={{ fontSize: 12, marginTop: 4,
                              color: "var(--oh-text-secondary)" }}>
                  Votre description dictée est conservée telle quelle.
                </div>
              </div>
            )}

            {retenu && !aDepartager && (
              <div style={{ marginTop: 6, fontSize: 14 }}>
                <div style={{ fontWeight: 500 }}>{retenu.designation}</div>
                <div style={{ fontSize: 12, color: "var(--oh-text-secondary)" }}>
                  Réf. {retenu.reference} · {retenu.prix_ht} € HT
                  {retenu.unite ? ` / ${retenu.unite}` : ""} · TVA{" "}
                  {retenu.taux_tva} %
                  {retenu.prix_provisoire ? " · tarif PROVISOIRE" : ""}
                </div>
              </div>
            )}

            {!sansProduit && (
              <div style={{ display: "flex", alignItems: "center", gap: 8,
                            marginTop: 8 }}>
                <span style={{ fontSize: 14 }}>Quantité</span>
                {/* ⛔ VIDE tant que rien n'a été dicté ni saisi. Pré-remplir
                    « 1 » ferait passer une quantité INVENTÉE pour une
                    quantité relevée. */}
                <input type="number" inputMode="decimal" min="0" step="any"
                       value={q} aria-label={`Quantité pour ${p.enonce}`}
                       data-testid="mobile-devis-quantite"
                       onChange={(e) => majChoix(i, { quantite: e.target.value })}
                       style={{ width: 96, minHeight: 44, borderRadius: 10,
                                padding: "0 10px", fontSize: 16,
                                border: "0.5px solid var(--oh-border)",
                                background: "var(--oh-bg-surface)",
                                color: "var(--oh-text)" }} />
                <span style={{ fontSize: 14,
                               color: "var(--oh-text-secondary)" }}>
                  {p.unite || retenu?.unite || ""}
                </span>
              </div>
            )}

            <button style={{ ...T.tactile, minHeight: 40, marginTop: 8,
                             justifyContent: "center", fontSize: 14 }}
                    data-testid="mobile-devis-refuser"
                    onClick={() => majChoix(i, { exclue: !exclue(i) })}>
              {exclue(i) ? "Remettre cette ligne" : "Refuser cette ligne"}
            </button>
          </div>
        );
      })}

      {/* 🔴 « COMPLÉTER » — une prestation que Mario AJOUTE lui-même.
          ⚠️ Disponible dès que la structuration a eu lieu, MÊME si elle n'a
          rien reconnu : c'est justement le cas où Mario a le plus besoin
          d'ajouter (mesuré — son descriptif réel rend 0 produit apparié). */}
      {prop && (
        <div style={{ marginTop: 12 }} data-testid="mobile-devis-ajouts">
          {ajouts.map((a, i) => (
            <div key={i} style={{ marginTop: 10, padding: 10, borderRadius: 10,
                                  border: "0.5px dashed var(--oh-accent)" }}
                 data-testid="mobile-devis-ajout">
              <div style={{ fontSize: 12, marginBottom: 6,
                            color: "var(--oh-text-secondary)" }}>
                Prestation ajoutée à la main
              </div>
              <input type="text" value={a.enonce}
                     placeholder="Décrivez la prestation"
                     aria-label="Description de la prestation ajoutée"
                     data-testid="mobile-devis-ajout-texte"
                     onChange={(e) => majAjout(i, { enonce: e.target.value })}
                     style={{ width: "100%", minHeight: 44, borderRadius: 10,
                              padding: "0 10px", fontSize: 16, marginBottom: 8,
                              border: "0.5px solid var(--oh-border)",
                              background: "var(--oh-bg-surface)",
                              color: "var(--oh-text)" }} />
              <select value={a.reference || ""}
                      aria-label="Poste catalogue de la prestation ajoutée"
                      data-testid="mobile-devis-ajout-poste"
                      onChange={(e) => majAjout(i, {
                        reference: e.target.value || null })}
                      style={{ width: "100%", minHeight: 44, borderRadius: 10,
                               padding: "0 10px", fontSize: 16, marginBottom: 8,
                               border: "0.5px solid var(--oh-border)",
                               background: "var(--oh-bg-surface)",
                               color: "var(--oh-text)" }}>
                <option value="">— Choisir le poste catalogue —</option>
                {catalogue.map((c) => (
                  <option key={c.reference} value={c.reference}>
                    {c.designation} · {c.reference}
                    {c.unite ? ` (${c.unite})` : ""}
                  </option>
                ))}
              </select>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14 }}>Quantité</span>
                {/* ⛔ VIDE au départ — ⛔ jamais « 1 ». Une quantité inventée
                    est exactement ce qu'on refuse depuis le début. */}
                <input type="number" inputMode="decimal" min="0" step="any"
                       value={a.quantite}
                       aria-label="Quantité de la prestation ajoutée"
                       data-testid="mobile-devis-ajout-quantite"
                       onChange={(e) => majAjout(i, { quantite: e.target.value })}
                       style={{ width: 96, minHeight: 44, borderRadius: 10,
                                padding: "0 10px", fontSize: 16,
                                border: "0.5px solid var(--oh-border)",
                                background: "var(--oh-bg-surface)",
                                color: "var(--oh-text)" }} />
                <button style={{ ...T.tactile, minHeight: 40, width: "auto",
                                 flex: 1, justifyContent: "center",
                                 fontSize: 14 }}
                        data-testid="mobile-devis-ajout-retirer"
                        onClick={() => setAjouts((l) =>
                          l.filter((_, k) => k !== i))}>
                  Retirer
                </button>
              </div>
            </div>
          ))}
          <button style={{ ...T.tactile, justifyContent: "center",
                           marginTop: 10 }}
                  data-testid="mobile-devis-ajouter"
                  onClick={() => setAjouts((l) =>
                    [...l, { enonce: "", reference: null, quantite: "" }])}>
            <Plus size={18} /> Ajouter une prestation
          </button>
        </div>
      )}

      {prop && (prop.propositions.length > 0 || ajouts.length > 0) && (
        <>
          <div style={{ marginTop: 10, fontSize: 13,
                        color: "var(--oh-text-secondary)" }}
               data-testid="mobile-devis-recap">
            {retenues.length} ligne(s) seront créées sur{" "}
            {prop.propositions.length} dictée(s)
            {ajouts.length > 0 && ` et ${ajouts.length} ajout(s)`}.
          </div>
          <button style={{ ...T.tactile,
                           ...(retenues.length ? T.principal : {}),
                           justifyContent: "center", marginTop: 8 }}
                  disabled={!retenues.length || occupe}
                  data-testid="mobile-devis-creer" onClick={creer}>
            <Check size={20} /> Créer le devis
          </button>
        </>
      )}
    </div>
  );
}

/** 🔴 PREMIER TEMPS DU GUIDE — « Choisir les travaux concernés ».
 *
 * 🔑 CE QUE CET ÉCRAN ÉVITE, mesuré : sans lui, le parcours traversait les SIX
 * sections et réclamait UNE PHOTO PAR SECTION — y compris pour des travaux que
 * Mario ⛔ n'avait jamais retenus. La règle du profil s'appelle pourtant
 * « chaque zone RETENUE exige au moins une photo » : le mot « retenue »
 * ⛔ n'existait NULLE PART dans le code.
 */
function ChoixTravaux({ visite, activables, occupe, erreur, onRetenir }) {
  const [choisis, setChoisis] = useState([]);
  const bascule = (id) =>
    setChoisis((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  return (
    <div style={T.page} data-testid="mobile-choix-travaux">
      <div style={T.barre}>
        <div style={{ fontSize: 12, color: "var(--oh-text-secondary)" }}>
          {visite.dossier_reference}
        </div>
        <div style={{ fontWeight: 500, fontSize: 17 }}>
          Choisir les travaux concernés
        </div>
        <div style={{ fontSize: 12, color: "var(--oh-text-secondary)" }}>
          Seuls les travaux retenus seront parcourus.
        </div>
      </div>

      {erreur && <Erreur texte={erreur} />}

      <div style={T.carte}>
        {activables.map((x) => {
          const on = choisis.includes(x.id);
          return (
            <button key={x.id} type="button"
                    data-testid={`mobile-travaux-${x.id}`}
                    aria-pressed={on}
                    onClick={() => bascule(x.id)}
                    style={{ ...T.tactile, width: "100%", marginBottom: 8,
                             justifyContent: "flex-start", textAlign: "left",
                             background: on ? "var(--oh-accent-bg)"
                                            : "var(--oh-bg-elevated)",
                             color: on ? "var(--oh-accent-text)"
                                       : "var(--oh-text)",
                             border: `1px solid ${on ? "var(--oh-accent)"
                                                     : "var(--oh-border)"}` }}>
              {on ? <Check size={20} /> : <Square size={20} />}
              <span style={{ fontWeight: 500 }}>{x.libelle}</span>
            </button>
          );
        })}
      </div>

      {/* 🔴 LE COMPTE EST DIT AVANT DE CONTINUER : Mario doit VOIR ce qu'il
          engage, ⛔ pas le découvrir étape par étape. */}
      <div style={T.carte}>
        <div style={{ fontSize: 13, color: "var(--oh-text-secondary)",
                      marginBottom: 10, lineHeight: 1.6 }}>
          {choisis.length === 0
            ? "Sélectionnez au moins un type de travaux pour continuer."
            : `${choisis.length} type(s) de travaux retenu(s). Les autres `
              + "ne seront pas parcourus et ne demanderont aucune photo."}
        </div>
        <button style={{ ...T.tactile, ...T.principal, width: "100%",
                         justifyContent: "center",
                         opacity: (occupe || !choisis.length) ? 0.5 : 1 }}
                data-testid="mobile-travaux-continuer"
                disabled={occupe || !choisis.length}
                onClick={() => onRetenir(choisis)}>
          Continuer
        </button>
      </div>
    </div>
  );
}

function Erreur({ texte }) {
  return (
    <div style={{ ...T.carte, background: "var(--oh-stat-red-bg)",
                  color: "var(--oh-stat-red-text)", whiteSpace: "pre-line",
                  fontSize: 14 }}
         data-testid="mobile-erreur">
      {texte}
    </div>
  );
}

function ChoixDossier({ dossiers, profil, occupe, onDemarrer }) {
  const [dossier, setDossier] = useState("");
  const [devis, setDevis] = useState("");
  // ⚠️ Le devis n'est demandé QUE pour un profil à devis en ENTRÉE — c'est
  // le profil qui le dit, ⛔ pas l'écran.
  const entree = profil.type_visite === "recherche_fuite";
  return (
    <>
      <select value={dossier} onChange={(e) => setDossier(e.target.value)}
              style={{ ...T.tactile, marginBottom: 10 }}
              aria-label="Dossier" data-testid={`mobile-dossier-${profil.code}`}>
        <option value="">Choisir un dossier…</option>
        {dossiers.map((d) => (
          <option key={d.id || d._id} value={d.id || d._id}>
            {d.reference || d.id}
          </option>
        ))}
      </select>
      {/* 🔴 LE DEVIS EST ANNONCÉ « (requis) » — LE BOUTON L'HONORE.
          ⚠️ Mesuré AU DOIGT sur la version servie : le champ disait « Devis
          accepté (requis) » et le bouton « Démarrer » restait ACTIF sans lui.
          Mario appuyait, et le refus ⛔ n'arrivait QU'APRÈS, du serveur.
          🔑 Un champ dit requis dont l'absence ⛔ n'empêche RIEN apprend à
          l'utilisateur que « requis » ⛔ ne veut rien dire. */}
      {entree && (
        <input value={devis} onChange={(e) => setDevis(e.target.value)}
               placeholder="Devis accepté (requis)" style={{ ...T.tactile, marginBottom: 10 }}
               aria-label="Devis accepté" data-testid="mobile-devis" />
      )}
      <button style={{ ...T.tactile, ...T.principal, justifyContent: "center" }}
              disabled={!dossier || occupe || (entree && !devis.trim())}
              data-testid={`mobile-demarrer-${profil.code}`}
              onClick={() => onDemarrer(dossier, profil.code, devis)}>
        {occupe ? <Loader2 size={18} className="animate-spin" /> : "Démarrer la visite"}
      </button>
    </>
  );
}

function Question({ q, section, visite, onRepondre }) {
  const r = ((visite.reponses || {})[section.id] || {})[q.id] || {};
  const commun = { ...T.tactile, marginBottom: 10 };
  if (q.type === "case") {
    const coche = !!r.valeur;
    return (
      <>
        <button style={{ ...commun, background: coche
                          ? "var(--oh-stat-green-bg)" : "var(--oh-bg-surface)",
                         color: coche ? "var(--oh-stat-green-text)" : "var(--oh-text)" }}
                data-testid={`mq-${section.id}-${q.id}`}
                onClick={() => {
                  // 🔴 DÉCOCHER ENVOIE `null`, ⛔ PAS UNE CHAÎNE VIDE.
                  //
                  // ⚠️ MESURÉ le 18/09 sur le servi : ce geste envoyait
                  // `quantite: ""`. Le parent fait `quantite ?? null` — et
                  // `"" ?? null` vaut `""`, ⛔ PAS `null`. Le modèle attend
                  // `Optional[float]` ⇒ 422 Pydantic, dont le `detail` est une
                  // LISTE ⇒ l'écran affichait `[object Object]`… ou RIEN.
                  // 🔴 RÉSULTAT : la prestation restait COCHÉE EN BASE. Une
                  // erreur de doigt condamnait la visite à traîner un bouquet
                  // et sa photo obligatoire.
                  // 🔑 CONTRE-ÉPREUVE MESURÉE : le même décochage avec `null`
                  // rend 200 et `valeur = False`. C'est bien la chaîne vide.
                  if (!coche) { onRepondre(true, undefined); return; }
                  // ⚠️ DÉCOCHER PERD LA QUANTITÉ DÉJÀ SAISIE : on le DIT.
                  // ⛔ Jamais un clic destructeur en silence.
                  if (r.quantite !== undefined && r.quantite !== null) {
                    const ok = window.confirm(
                      `Retirer « ${q.libelle} » ? La quantité saisie `
                      + `(${r.quantite}) sera perdue.`);
                    if (!ok) return;
                  }
                  onRepondre(false, null);
                }}>
          {coche ? <Check size={20} /> : <span style={{ width: 20 }} />}
          {q.libelle}
        </button>
        {/* 🔴 La quantité n'apparaît QUE si le travail est retenu : elle n'a de
            sens que là, et c'est elle qui produira la ligne de devis. */}
        {coche && (
          <input type="number" inputMode="decimal" step="0.01"
                 defaultValue={r.quantite ?? ""}
                 placeholder={`Quantité (${q.unite_quantite || "u"})`}
                 style={{ ...commun }} aria-label={`Quantité pour ${q.libelle}`}
                 data-testid={`mqte-${section.id}-${q.id}`}
                 onBlur={(e) => onRepondre(true, e.target.value === ""
                   ? null : Number(e.target.value))} />
        )}
      </>
    );
  }
  if (q.type === "choix") {
    return (
      <select defaultValue={r.valeur ?? ""} style={commun} aria-label={q.libelle}
              data-testid={`mq-${section.id}-${q.id}`}
              onChange={(e) => onRepondre(e.target.value)}>
        <option value="">{q.libelle}</option>
        {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  // ⚠️ « Surface concernée (m²) (m²) » — vu À L'ÉCRAN, ⛔ pas en test.
  // Le libellé du profil porte DÉJÀ l'unité, et on la rajoutait derrière.
  // 🔑 Corrigé ICI, ⛔ pas dans la donnée : un métier écrira naturellement
  // « Surface (m²) » dans son libellé — c'est au moteur de le tolérer,
  // ⛔ pas à lui d'apprendre à ne pas l'écrire.
  const invite = q.libelle + (q.unite && !q.libelle.includes(q.unite)
                              ? ` (${q.unite})` : "");
  return (
    <input type={q.type === "nombre" ? "number" : "text"}
           inputMode={q.type === "nombre" ? "decimal" : undefined}
           defaultValue={r.valeur ?? ""}
           placeholder={invite}
           style={commun} aria-label={q.libelle}
           data-testid={`mq-${section.id}-${q.id}`}
           onBlur={(e) => onRepondre(q.type === "nombre"
             ? Number(e.target.value) : e.target.value)} />
  );
}

function Observations({ visite, section, onEnregistrer }) {
  const initial = ((visite.observations || {})[section.id] || {}).texte || "";
  const [texte, setTexte] = useState(initial);
  useEffect(() => { setTexte(initial); }, [initial, section.id]);
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8,
                    marginBottom: 6 }}>
        <span style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
          Observations
        </span>
        {/* 🔑 LA DICTÉE — on parle plus vite qu'on ne tape, et sur un chantier
            on tape mal. `MicButton` existait déjà : c'est de l'ADAPT.
            ⚠️ À VÉRIFIER SUR LES APPAREILS CIBLÉS : `SpeechRecognition` évite
            une clé ET un appel à NOTRE backend, ⛔ mais selon le navigateur et
            l'OS la reconnaissance peut être traitée par un service DU
            FOURNISSEUR DU NAVIGATEUR. ⛔ « La voix ne quitte jamais le
            téléphone » ⛔ N'EST PAS acquis sans test — Chrome Android notamment
            est réputé traiter côté serveur. À mesurer avant de le promettre. */}
        <MicButton value={texte} onTranscript={(t) => { setTexte(t); }} />
      </div>
      <textarea value={texte} onChange={(e) => setTexte(e.target.value)}
                onBlur={() => texte !== initial && onEnregistrer(texte)}
                rows={3} placeholder="Dicter ou saisir…"
                aria-label={`Observations pour ${section.libelle}`}
                data-testid={`mobs-${section.id}`}
                style={{ ...T.tactile, minHeight: 84, padding: 12,
                         alignItems: "flex-start" }} />
    </div>
  );
}

function Reserves({ visite, section, config, onAjouter }) {
  const [ouvert, setOuvert] = useState(false);
  const [libelle, setLibelle] = useState("");
  const [gravite, setGravite] = useState("");
  const miennes = (visite.reserves_ouvertes || [])
    .filter((r) => r.section_id === section.id);
  return (
    <div style={{ marginTop: 12, borderTop: "0.5px solid var(--oh-border)",
                  paddingTop: 12 }}>
      {/* 🔴 LA BLOQUANTE SE VOIT, ⛔ elle ne se DÉDUIT pas.
          ⚠️ Mesuré à l'écran avec 4 réserves : les 4 sortaient dans le MÊME
          ambre. Pour savoir laquelle bloque, Mario devait LIRE le libellé de
          gravité ET savoir laquelle est bloquante — une connaissance qui vit
          dans le PROFIL, ⛔ pas à l'écran. Au pouce, dans un logement, une main
          prise, ça ⛔ ne se fait pas.
          🔑 Le drapeau `bloquante` est porté par LA RÉSERVE (copié depuis
          l'échelle du profil au moment de sa création) : l'écran ⛔ n'a AUCUN
          nom de gravité en dur, et un autre métier avec une autre échelle
          obtient le même comportement. */}
      {miennes.map((r) => (
        <div key={r.id} style={{ fontSize: 13, marginBottom: 6,
                                 color: r.bloquante
                                   ? "var(--oh-stat-red-text)"
                                   : "var(--oh-stat-amber-text)" }}
             data-testid={r.bloquante ? "mobile-reserve-bloquante"
                                      : "mobile-reserve-simple"}>
          <Flag size={12} /> {r.libelle} · {r.gravite_libelle}
          {r.bloquante && (
            <span style={{ marginLeft: 6, fontWeight: 500, whiteSpace: "nowrap",
                           background: "var(--oh-stat-red-bg)",
                           borderRadius: 6, padding: "1px 6px" }}>
              bloque la clôture
            </span>
          )}
        </div>
      ))}
      {!ouvert ? (
        <button style={{ ...T.tactile, justifyContent: "center" }}
                data-testid="mobile-reserve-ouvrir"
                onClick={() => setOuvert(true)}>
          <Flag size={18} /> Ajouter une réserve
        </button>
      ) : (
        <>
          <input value={libelle} onChange={(e) => setLibelle(e.target.value)}
                 placeholder="Ce qui est constaté" aria-label="Libellé de la réserve"
                 style={{ ...T.tactile, marginBottom: 8 }}
                 data-testid="mobile-reserve-libelle" />
          {/* ⚠️ L'ÉCHELLE VIENT DU PROFIL — l'écran ⛔ ne l'invente PAS. */}
          <select value={gravite} onChange={(e) => setGravite(e.target.value)}
                  style={{ ...T.tactile, marginBottom: 8 }} aria-label="Gravité"
                  data-testid="mobile-reserve-gravite">
            <option value="">Gravité…</option>
            {/* 🔴 LA CONSÉQUENCE EST DITE AU MOMENT DU CHOIX, ⛔ pas après.
                ⚠️ Mesuré à l'écran : la liste ne proposait que « Observation ·
                Réserve · Réserve majeure ». Laquelle interdit la clôture
                ⛔ n'était écrite NULLE PART — Mario devait CONNAÎTRE LE PROFIL,
                et il découvrait la conséquence en bas de l'écran, le bouton
                déjà bloqué.
                🔑 Le texte vient de `g.bloquante`, ⛔ pas d'une liste de codes :
                un autre métier, avec ses propres niveaux, obtient le même
                service sans qu'on touche à l'écran. */}
            {(config.gravites || []).map((g) => (
              <option key={g.code} value={g.code}>
                {g.libelle}{g.bloquante ? " — bloque la clôture" : ""}
              </option>
            ))}
          </select>
          <button style={{ ...T.tactile, ...T.principal, justifyContent: "center" }}
                  disabled={!libelle.trim() || !gravite}
                  data-testid="mobile-reserve-valider"
                  onClick={() => { onAjouter(libelle, gravite);
                                   setLibelle(""); setGravite(""); setOuvert(false); }}>
            Consigner la réserve
          </button>
        </>
      )}
    </div>
  );
}

function Cloture({ visite, onCloturer }) {
  const manques = visite.manques || [];
  const ouvertes = visite.reserves_ouvertes || [];
  const bloquantes = visite.reserves_bloquantes || [];
  // 🔑 LE RAPPORT EST CONSULTABLE AVANT CLÔTURE — c'est précisément quand une
  // réserve bloque qu'il faut pouvoir montrer CE QUI BLOQUE.
  const lienRapport = (
    <a href={`/api/visite/saisies/${visite.id}/rapport`} target="_blank"
       rel="noreferrer" data-testid="mobile-rapport"
       style={{ ...T.tactile, justifyContent: "center", textDecoration: "none",
                marginTop: 8 }}>
      <FileText size={18} /> {visite.statut === "terminee"
        ? "Ouvrir le rapport" : "Voir le rapport provisoire"}
    </a>
  );
  if (visite.statut === "terminee") {
    return (
      <div style={T.carte}>
        <a href={`/api/visite/saisies/${visite.id}/rapport`} target="_blank"
           rel="noreferrer" data-testid="mobile-rapport"
           style={{ ...T.tactile, ...T.principal, justifyContent: "center",
                    textDecoration: "none" }}>
          <FileText size={20} /> Ouvrir le rapport
        </a>
      </div>
    );
  }
  return (
    <div style={T.carte}>
      {/* ⚠️ Les RÉSERVES sont affichées, ⛔ elles n'empêchent PAS de clôturer :
          on réceptionne AVEC. Les MANQUES, eux, bloquent. */}
      {/* 🔴 DEUX CHOSES DIFFÉRENTES, DEUX MESSAGES DIFFÉRENTS.
          Une réserve BLOQUANTE appelle une ACTION DE CHANTIER ; un manque
          appelle UNE SAISIE. Les confondre enverrait chercher un champ à
          remplir là où il faut une reprise. */}
      {bloquantes.length > 0 && (
        <div style={{ fontSize: 14, color: "var(--oh-stat-red-text)",
                      background: "var(--oh-stat-red-bg)", borderRadius: 10,
                      padding: 10, marginBottom: 8 }}
             data-testid="mobile-bloquantes">
          {/* 🔴 ON NOMME CE QUI BLOQUE. Le compte SEUL obligeait le visiteur à
              REMONTER tout le pas-à-pas pour savoir laquelle — et il lit ce
              message EN BAS, à l'instant précis où il veut clôturer.
              ⚠️ Défaut vu à l'épreuve NAVIGATEUR, ⛔ PAS en test de service :
              le desktop les listait déjà, le mobile ⛔ non. Un banc de service
              ⛔ ne pouvait pas le voir — il ⛔ ne lit pas un écran. */}
          <div style={{ fontWeight: 500, marginBottom: 4 }}>
            {bloquantes.length} réserve(s) BLOQUANTE(S) — à traiter ou à lever
            avant de clôturer. Le rapport reste consultable.
          </div>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {bloquantes.map((r) => (
              <li key={r.id} data-testid="mobile-bloquante-item">
                {r.libelle} — {r.gravite_libelle || r.gravite}
              </li>
            ))}
          </ul>
        </div>
      )}
      {ouvertes.length > bloquantes.length && (
        <div style={{ fontSize: 13, color: "var(--oh-stat-amber-text)",
                      marginBottom: 8 }}>
          {ouvertes.length - bloquantes.length} réserve(s) non bloquante(s) —
          elles partiront au rapport.
        </div>
      )}
      {manques.length > 0 && (
        <div style={{ fontSize: 13, color: "var(--oh-text-secondary)",
                      marginBottom: 8 }} data-testid="mobile-manques">
          {/* 🔴 ON LES NOMME TOUS. ⛔ Plus de « … ».
              ⚠️ Mesuré : 2 manques réels, UN SEUL affiché, le reste masqué par
              « … ». Mario est à l'étape 6 sur 6 et ⛔ ne sait NI combien
              d'étapes remonter NI lesquelles : il referme le pas-à-pas et le
              refait en entier pour trouver le champ vide.
              🔑 Chaque manque porte DÉJÀ sa section entre parenthèses — le
              serveur la donne, l'écran la jetait. */}
          <div style={{ fontWeight: 500, marginBottom: 4 }}>
            Il reste {manques.length} élément(s) à renseigner
          </div>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {manques.map((m, i) => (
              <li key={i} data-testid="mobile-manque-item">{m}</li>
            ))}
          </ul>
        </div>
      )}
      <button style={{ ...T.tactile,
                       ...((manques.length || bloquantes.length) ? {} : T.principal),
                       justifyContent: "center" }}
              disabled={manques.length > 0 || bloquantes.length > 0}
              data-testid="mobile-cloturer"
              onClick={onCloturer}>
        <Check size={20} /> Clôturer la visite
      </button>
      {lienRapport}
    </div>
  );
}
